/**
 * CW-SF-M5 TKT-3 — "My Open Opportunities" widget.
 *
 * Top-N open opps the user owns, sorted by CloseDate ASC. Each row:
 * stage chip + amount + days-to-close countdown + deep link.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { daysUntil, formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface OppItem {
  external_file_id: string;
  name: string;
  stage: string | null;
  amount: number | null;
  close_date: string | null;
  web_url: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: OppItem[];
}

const REFRESH_S = 5 * 60; // 5min auto-refresh per sprint spec

export function SfMyOpenOpportunitiesWidget(_props: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>('/mod/cw/widgets/sf/my-open-opportunities?limit=10'),
    [],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--opps">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage="No open opportunities."
      >
        <ul className="sf-widget__list">
          {data?.items.map((o) => {
            const days = daysUntil(o.close_date);
            return (
              <li key={o.external_file_id} className="sf-widget__row">
                <a
                  href={o.web_url ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sf-widget__row-name"
                >
                  {o.name}
                  <ExternalLink size={10} />
                </a>
                <div className="sf-widget__row-chips">
                  {o.stage && <span className="sf-widget__chip">{o.stage}</span>}
                  {o.amount !== null && (
                    <span className="sf-widget__chip sf-widget__chip--amount">
                      {formatCurrency(o.amount)}
                    </span>
                  )}
                  {days !== null && (
                    <span
                      className={
                        days < 0
                          ? 'sf-widget__chip sf-widget__chip--overdue'
                          : 'sf-widget__chip'
                      }
                    >
                      {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
